import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  acknowledgeTimeClockLate,
  fetchTimeClockScheduleStatus,
  justifyTimeClockBreak,
  justifyTimeClockLate,
  justifyTimeClockStay,
} from "../../api/client";
import { playVoPresenceSound } from "./virtualOfficePresenceSounds";

const POLL_MS = 30000;
const NAG_MS = 90 * 1000;

export function useVirtualOfficeJornadaAlerts({
  enabled = false,
  present = false,
  initialSchedule = null,
} = {}) {
  const [schedule, setSchedule] = useState(initialSchedule);
  const [lateOpen, setLateOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [breakOpen, setBreakOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [justifyMode, setJustifyMode] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [compensation, setCompensation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lastNagRef = useRef(0);

  useEffect(() => {
    if (initialSchedule) setSchedule(initialSchedule);
  }, [initialSchedule]);

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    try {
      const res = await fetchTimeClockScheduleStatus({ present: present ? "1" : "0" });
      const next = res?.data?.data || res?.data || null;
      if (next) setSchedule(next);
      return next;
    } catch {
      return null;
    }
  }, [enabled, present]);

  useEffect(() => {
    if (!enabled) {
      setLateOpen(false);
      setExitOpen(false);
      setBreakOpen(false);
      setReturnOpen(false);
      setJustifyMode(null);
      return undefined;
    }
    void refresh();
    const id = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    const candidates = [
      schedule?.exitDue?.msUntilDue,
      schedule?.breakDue?.msUntilDue,
      schedule?.breakReturn?.msUntilDue,
    ]
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!candidates.length) return undefined;
    const id = setTimeout(() => void refresh(), Math.min(Math.min(...candidates) + 1500, 30 * 60 * 1000));
    return () => clearTimeout(id);
  }, [
    enabled,
    refresh,
    schedule?.breakDue?.msUntilDue,
    schedule?.breakReturn?.msUntilDue,
    schedule?.exitDue?.msUntilDue,
  ]);

  useEffect(() => {
    if (!enabled || !schedule?.applies) {
      setLateOpen(false);
      setExitOpen(false);
      setBreakOpen(false);
      setReturnOpen(false);
      return;
    }
    const exitDue = Boolean(present && schedule.exitDue?.isDue && !schedule.exitDue?.stayJustified);
    const returnDue = Boolean(schedule.breakReturn?.isDue);
    const breakDue = Boolean(present && schedule.breakDue?.isDue);
    const lateDue = Boolean(present && schedule.late?.isLate && !schedule.late?.acknowledged);
    if (exitDue) {
      setExitOpen(true);
      setBreakOpen(false);
      setReturnOpen(false);
      setLateOpen(false);
      return;
    }
    if (returnDue) {
      setExitOpen(false);
      setReturnOpen(true);
      setBreakOpen(false);
      setLateOpen(false);
      return;
    }
    if (breakDue) {
      setExitOpen(false);
      setReturnOpen(false);
      setBreakOpen(true);
      setLateOpen(false);
      return;
    }
    setExitOpen(false);
    setReturnOpen(false);
    setBreakOpen(false);
    setLateOpen(lateDue);
  }, [enabled, present, schedule]);

  const nagging = exitOpen || breakOpen || returnOpen;
  useEffect(() => {
    if (!nagging) return undefined;
    const tick = () => {
      const now = Date.now();
      if (now - lastNagRef.current < NAG_MS) return;
      lastNagRef.current = now;
      playVoPresenceSound(returnOpen ? "enter" : breakOpen ? "leave_break" : "leave_exit");
    };
    tick();
    const id = setInterval(tick, NAG_MS);
    return () => clearInterval(id);
  }, [breakOpen, exitOpen, nagging, returnOpen]);

  const acknowledgeLate = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const res = await acknowledgeTimeClockLate();
      const next = res?.data?.data?.scheduleStatus || res?.data?.scheduleStatus;
      if (next) setSchedule(next);
      setLateOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível registrar.");
    } finally {
      setBusy(false);
    }
  }, []);

  const submitJustifyLate = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const res = await justifyTimeClockLate({ justificativa });
      const next = res?.data?.data?.scheduleStatus || res?.data?.scheduleStatus;
      if (next) setSchedule(next);
      setJustifyMode(null);
      setJustificativa("");
      setLateOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível justificar.");
    } finally {
      setBusy(false);
    }
  }, [justificativa]);

  const submitJustifyStay = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const res = await justifyTimeClockStay({
        justificativa,
        compensation: compensation || schedule?.compensation?.overtimeDestination || undefined,
      });
      const next = res?.data?.data?.scheduleStatus || res?.data?.scheduleStatus;
      if (next) setSchedule(next);
      setJustifyMode(null);
      setJustificativa("");
      setExitOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível justificar.");
    } finally {
      setBusy(false);
    }
  }, [compensation, justificativa, schedule?.compensation?.overtimeDestination]);

  const submitJustifyBreak = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const res = await justifyTimeClockBreak({ justificativa });
      const next = res?.data?.data?.scheduleStatus || res?.data?.scheduleStatus;
      if (next) setSchedule(next);
      setJustifyMode(null);
      setJustificativa("");
      setBreakOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível justificar.");
    } finally {
      setBusy(false);
    }
  }, [justificativa]);

  const openStayJustify = useCallback(() => {
    setCompensation(schedule?.compensation?.overtimeDestination || "hours_bank");
    setJustificativa("");
    setError("");
    setJustifyMode("stay");
  }, [schedule?.compensation?.overtimeDestination]);

  const openLateJustify = useCallback(() => {
    setJustificativa("");
    setError("");
    setJustifyMode("late");
  }, []);

  const openBreakJustify = useCallback(() => {
    setJustificativa("");
    setError("");
    setJustifyMode("break");
  }, []);

  const approaching = useMemo(() => {
    if (!schedule?.applies) return false;
    if (schedule.exitDue?.isDue || schedule.breakDue?.isDue || schedule.breakReturn?.isDue) return false;
    const windows = [schedule.exitDue?.msUntilDue, schedule.breakDue?.msUntilDue].map((n) => Number(n));
    return windows.some((ms) => Number.isFinite(ms) && ms > 0 && ms <= 30 * 60 * 1000);
  }, [schedule]);

  return {
    schedule,
    lateOpen,
    exitOpen,
    breakOpen,
    returnOpen,
    justifyMode,
    justificativa,
    setJustificativa,
    compensation,
    setCompensation,
    busy,
    error,
    approaching,
    acknowledgeLate,
    submitJustifyLate,
    submitJustifyStay,
    submitJustifyBreak,
    openStayJustify,
    openLateJustify,
    openBreakJustify,
    closeJustify: () => {
      setJustifyMode(null);
      setError("");
    },
    refresh,
  };
}
